import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { DASHBOARD } from '@/constants/testIds';
import { TrendUp, Package, Users, Stethoscope, Warning, CurrencyInr } from '@phosphor-icons/react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getDashboard().then(r => { setStats(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-center" style={{ color: '#8A94A6' }}>Loading dashboard...</div>;
  if (!stats) return <div className="p-8 text-center" style={{ color: '#FF3B30' }}>Failed to load dashboard</div>;

  const cards = [
    { label: 'Total Revenue', value: `₹${(stats.total_revenue || 0).toLocaleString()}`, icon: CurrencyInr, color: '#007AFF', testId: DASHBOARD.totalRevenue },
    { label: 'Today Sales', value: stats.today_sales, icon: TrendUp, color: '#34C759', testId: DASHBOARD.totalSales },
    { label: 'Medicines', value: stats.total_medicines, icon: Package, color: '#0A2540', testId: DASHBOARD.totalMedicines },
    { label: 'Customers', value: stats.total_customers, icon: Users, color: '#007AFF', testId: DASHBOARD.totalCustomers },
    { label: 'Doctors', value: stats.total_doctors, icon: Stethoscope, color: '#34C759' },
    { label: 'Low Stock', value: stats.low_stock_count, icon: Warning, color: '#FF3B30', testId: DASHBOARD.lowStock },
  ];

  return (
    <div data-testid={DASHBOARD.container} className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>Dashboard</h1>
        <p className="text-sm mt-1" style={{ color: '#8A94A6', fontFamily: "'IBM Plex Sans', sans-serif" }}>Overview of your pharmacy operations</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.label}
              data-testid={card.testId}
              className="p-4 rounded-md border"
              style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon size={18} weight="duotone" style={{ color: card.color }} />
                <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{card.label}</span>
              </div>
              <p className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540', fontVariantNumeric: 'tabular-nums' }}>
                {card.value}
              </p>
            </div>
          );
        })}
      </div>

      {/* Revenue Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="p-4 rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
          <p className="text-[10px] font-semibold uppercase tracking-widest mb-1" style={{ color: '#8A94A6' }}>Total Received</p>
          <p className="text-2xl font-bold" style={{ color: '#34C759', fontVariantNumeric: 'tabular-nums' }}>₹{(stats.total_received || 0).toLocaleString()}</p>
        </div>
        <div className="p-4 rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
          <p className="text-[10px] font-semibold uppercase tracking-widest mb-1" style={{ color: '#8A94A6' }}>Total Due</p>
          <p className="text-2xl font-bold" style={{ color: '#FF3B30', fontVariantNumeric: 'tabular-nums' }}>₹{(stats.total_due || 0).toLocaleString()}</p>
        </div>
        <div className="p-4 rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
          <p className="text-[10px] font-semibold uppercase tracking-widest mb-1" style={{ color: '#8A94A6' }}>Expiring Soon</p>
          <p className="text-2xl font-bold" style={{ color: '#FFCC00', fontVariantNumeric: 'tabular-nums' }}>{stats.expiring_soon}</p>
        </div>
      </div>

      {/* Last 3 Months Sales Trend */}
      <div data-testid={DASHBOARD.salesChart} className="p-4 rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
        <h3 className="text-sm font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>Last 3 Months Sales Trend</h3>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={stats.monthly_trend || []}>
            <defs>
              <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#007AFF" stopOpacity={0.15} />
                <stop offset="95%" stopColor="#007AFF" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E4E9" />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#8A94A6' }} />
            <YAxis tick={{ fontSize: 11, fill: '#8A94A6' }} />
            <Tooltip contentStyle={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, borderRadius: 4, borderColor: '#E2E4E9' }} />
            <Area type="monotone" dataKey="revenue" stroke="#007AFF" fillOpacity={1} fill="url(#colorRev)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
